import { Injectable, inject } from '@angular/core';
import {
  AssetService,
  GridUtilsV2,
  SpinnerV2Service,
  StorageV2Service,
} from '@schenck/rotec-components';
import { combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { statusFilter } from 'src/components/status/status.component';
import { SearchServiceBase, SearchApi } from 'src/services/search.service';
import { ToolingTaskSettingsService } from './tooling-task-settings.service';
import { ToolingTask } from './tooling-tasks.model';
import { ToolingTaskService } from 'src/services';
import { gridSchedulingMultiselectFilter } from 'src/components/task-interval.pipe';

@Injectable()
export class ToolingTaskSearchApi implements SearchApi<ToolingTask> {
  private readonly taskService = inject(ToolingTaskService);
  private readonly assetService = inject(AssetService);

  reload$ = this.taskService.taskChange$;

  search$(date: { from: Date; to: Date } | null) {
    return this.taskService.search$(true, date).pipe(
      switchMap((results) => {
        if (results.length === 0) {
          // necessary to handle this because combineLatest won't emit anything if it gets passed an empty array
          return of([]);
        }

        const searchResults$ = results.map((result) => {
          const tooling$ = this.assetService.tooling$(result.toolingId);
          const machines$ = this.assetService.machinesThatHaveTooling$(tooling$);

          return combineLatest([tooling$, machines$]).pipe(
            map(([tooling, machines]): ToolingTask => {
              if (!tooling) {
                return {
                  ...result,
                  machines: '',
                  toolingName: '',
                };
              }
              return {
                ...result,
                machines: machines
                  .map((machine) => machine.name + ' (' + machine.serial + ')')
                  .join(', '),
                toolingName: tooling.name,
                serial: tooling.serial,
                inventoryNo: tooling.inventoryNo,
                mainContact: tooling.mainContact,
                manufacturer: tooling.manufacturer,
                verificationLevel: tooling.verificationLevel,
                schenckMatNo: tooling.schenckMatNo,
                storageLocation: tooling.storageLocation,
                workplace: tooling.workplace,
                toolingForRotor: tooling.toolingForRotor,
              };
            })
          );
        });

        return combineLatest(searchResults$);
      })
    );
  }
}

@Injectable()
export class ToolingTaskSearchService extends SearchServiceBase<ToolingTask> {
  constructor() {
    const searchApi = inject(ToolingTaskSearchApi);
    const loadingService = inject(SpinnerV2Service);
    const settingsService = inject(ToolingTaskSettingsService);
    const storageService = inject(StorageV2Service);

    super(searchApi, loadingService, settingsService, storageService, {
      key: 'ToolingTasksSearch',
      primaryDateProp: 'dueDate',
      filter: {
        toolingName: GridUtilsV2.multiselectFilter,
        manufacturer: GridUtilsV2.multiselectFilter,
        inventoryNo: GridUtilsV2.textFilter,
        serial: GridUtilsV2.textFilter,
        schenckMatNo: GridUtilsV2.textFilter,
        verificationLevel: GridUtilsV2.multiselectFilter,
        customerId: GridUtilsV2.textFilter,
        schenckId: GridUtilsV2.textFilter,
        title: GridUtilsV2.textFilter,
        priority: GridUtilsV2.checkboxFilter,
        responsibleCompany: GridUtilsV2.radioFilter,
        responsible: GridUtilsV2.textFilter,
        executive: GridUtilsV2.textFilter,
        dueDate: GridUtilsV2.dateRangeFilter,
        status: statusFilter,
        description: GridUtilsV2.textFilter,
        type: GridUtilsV2.multiselectFilter,
        scheduling: gridSchedulingMultiselectFilter,
        machines: GridUtilsV2.textFilter, // TODO multiselect?
        toolingForRotor: GridUtilsV2.textFilter,
        mainContact: GridUtilsV2.textFilter,
        storageLocation: GridUtilsV2.textFilter,
        workplace: GridUtilsV2.textFilter,
      },
    });
  }
}
